import type { Identifier } from "ra-core";

import type { GulaPayment } from "../gulaTypes";
import {
  INCOME_METHODS,
  deductibleOf,
  labelFor,
  round2,
  splitVatIncluded,
  type AccExpense,
  type AccIncome,
  type AccSupplier,
} from "./accounting";

export type Quarter = 1 | 2 | 3 | 4;
export type Period = { year: number; quarter: Quarter | null };

const TIME_ZONE = "Europe/Madrid";

/** YYYY-MM-DD of a timestamp in Madrid (a 23:30 UTC payment is the next day). */
export const madridDay = (timestamp: string | Date): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(timestamp));

export const quarterOf = (day: string): Quarter =>
  (Math.floor((Number(day.slice(5, 7)) - 1) / 3) + 1) as Quarter;

export const inPeriod = (day: string | null | undefined, period: Period) => {
  if (!day || Number(day.slice(0, 4)) !== period.year) return false;
  return period.quarter == null || quarterOf(day) === period.quarter;
};

/** YYYY-MM of every month in the period. */
export const periodMonths = (period: Period): string[] => {
  const first = period.quarter ? (period.quarter - 1) * 3 + 1 : 1;
  const count = period.quarter ? 3 : 12;
  return Array.from(
    { length: count },
    (_, index) => `${period.year}-${String(first + index).padStart(2, "0")}`,
  );
};

export const periodLabel = (period: Period) =>
  period.quarter ? `${period.quarter}T ${period.year}` : `Año ${period.year}`;

/** Last day to file the quarterly VAT return (modelo 303). */
export const vatDeadline = (year: number, quarter: Quarter): string =>
  quarter === 4
    ? `${year + 1}-01-30`
    : `${year}-${String(quarter * 3 + 1).padStart(2, "0")}-20`;

const STRIPE_KIND_LABEL: Record<string, string> = {
  equipment: "Equipo",
  rental: "Alquiler",
  maintenance: "Mantenimiento",
  installments: "Cuota de pago a plazos",
  other: "Otro cobro",
};

/** One income line, from Stripe or added by hand. */
export type IncomeRow = {
  key: string;
  source: "stripe" | "manual";
  day: string;
  contactId?: Identifier | null;
  /** Gula client, to open the client sheet */
  clientId?: Identifier | null;
  customer: string;
  concept: string;
  method: string;
  vatRate: number;
  base: number;
  vat: number;
  total: number;
  receiptUrl?: string | null;
  income?: AccIncome;
};

/**
 * Stripe payments as income lines (VAT included in the amount). Refunded payments
 * are left out: Stripe gives back the whole amount.
 */
export const stripeIncomeRows = (
  payments: GulaPayment[],
  vatRate: number,
  customerName: (contactId: Identifier) => string,
): IncomeRow[] =>
  payments
    .filter((payment) => payment.status === "paid" && payment.paid_at)
    .map((payment) => {
      const total = Number(payment.amount);
      const { base_amount, vat_amount } = splitVatIncluded(total, vatRate);
      return {
        key: `stripe-${payment.id}`,
        source: "stripe" as const,
        day: madridDay(payment.paid_at as string),
        contactId: payment.contact_id,
        customer: customerName(payment.contact_id),
        concept:
          payment.description?.trim() ||
          STRIPE_KIND_LABEL[payment.kind] ||
          "Cobro",
        method: "Stripe",
        vatRate,
        base: base_amount,
        vat: vat_amount,
        total,
        receiptUrl: payment.receipt_url,
      };
    });

export const manualIncomeRows = (incomes: AccIncome[]): IncomeRow[] =>
  incomes.map((income) => ({
    key: `manual-${income.id}`,
    source: "manual" as const,
    day: income.income_date,
    contactId: income.contact_id,
    customer: income.customer_name ?? "",
    concept: income.concept,
    method: labelFor(INCOME_METHODS, income.method),
    vatRate: Number(income.vat_rate),
    base: Number(income.base_amount),
    vat: Number(income.vat_amount),
    total: Number(income.total),
    income,
  }));

const sum = <T>(items: T[], value: (item: T) => number) =>
  round2(items.reduce((total, item) => total + value(item), 0));

export type VatSummary = {
  /** Output VAT of our sales, by rate */
  salesByRate: { rate: number; base: number; vat: number }[];
  salesVat: number;
  /** Reverse charge purchases: declared as output VAT and deducted as input VAT */
  reverseBase: number;
  reverseVat: number;
  domesticBase: number;
  domesticVat: number;
  /** Input VAT that can be deducted (domestic + reverse charge) */
  deductibleVat: number;
  outputVat: number;
  /** Positive: to pay; negative: to offset or claim back */
  balance: number;
};

export type AccountingSummary = {
  incomeBase: number;
  incomeVat: number;
  incomeTotal: number;
  stripeBase: number;
  manualBase: number;
  incomeCount: number;
  expenseBase: number;
  deductibleBase: number;
  expenseTotal: number;
  expenseCount: number;
  result: number;
  vat: VatSummary;
  byCategory: { category: string; base: number; count: number }[];
  byMonth: {
    month: string;
    income: number;
    expenses: number;
    result: number;
  }[];
};

export const buildVatSummary = (
  incomes: IncomeRow[],
  expenses: AccExpense[],
): VatSummary => {
  const rates = [...new Set(incomes.map((row) => row.vatRate))].sort(
    (a, b) => b - a,
  );
  const salesByRate = rates.map((rate) => {
    const rows = incomes.filter((row) => row.vatRate === rate);
    return {
      rate,
      base: sum(rows, (row) => row.base),
      vat: sum(rows, (row) => row.vat),
    };
  });
  const reverse = expenses.filter((e) => e.vat_mode === "reverse_charge");
  const domestic = expenses.filter((e) => e.vat_mode === "normal");
  const salesVat = sum(salesByRate, (row) => row.vat);
  const reverseVat = sum(reverse, (e) => Number(e.vat_amount));
  const domesticVat = sum(domestic, (e) => deductibleOf(e).vat);
  const reverseDeductible = sum(reverse, (e) => deductibleOf(e).vat);
  const outputVat = round2(salesVat + reverseVat);
  const deductibleVat = round2(domesticVat + reverseDeductible);
  return {
    salesByRate,
    salesVat,
    reverseBase: sum(reverse, (e) => Number(e.base_amount)),
    reverseVat,
    domesticBase: sum(domestic, (e) => deductibleOf(e).base),
    domesticVat,
    deductibleVat,
    outputVat,
    balance: round2(outputVat - deductibleVat),
  };
};

export const buildSummary = (
  allIncomes: IncomeRow[],
  allExpenses: AccExpense[],
  period: Period,
): AccountingSummary => {
  const incomes = allIncomes.filter((row) => inPeriod(row.day, period));
  const expenses = allExpenses.filter((e) => inPeriod(e.invoice_date, period));
  const expenseBaseOf = (e: AccExpense) =>
    Number(e.base_amount) + Number(e.other_amount);

  const categories = new Map<string, { base: number; count: number }>();
  expenses.forEach((expense) => {
    const current = categories.get(expense.category) ?? { base: 0, count: 0 };
    categories.set(expense.category, {
      base: current.base + expenseBaseOf(expense),
      count: current.count + 1,
    });
  });

  const incomeBase = sum(incomes, (row) => row.base);
  const expenseBase = sum(expenses, expenseBaseOf);
  return {
    incomeBase,
    incomeVat: sum(incomes, (row) => row.vat),
    incomeTotal: sum(incomes, (row) => row.total),
    stripeBase: sum(
      incomes.filter((row) => row.source === "stripe"),
      (row) => row.base,
    ),
    manualBase: sum(
      incomes.filter((row) => row.source === "manual"),
      (row) => row.base,
    ),
    incomeCount: incomes.length,
    expenseBase,
    deductibleBase: sum(expenses, (e) => deductibleOf(e).base),
    expenseTotal: sum(expenses, (e) => Number(e.total)),
    expenseCount: expenses.length,
    result: round2(incomeBase - expenseBase),
    vat: buildVatSummary(incomes, expenses),
    byCategory: [...categories.entries()]
      .map(([category, value]) => ({
        category,
        base: round2(value.base),
        count: value.count,
      }))
      .sort((a, b) => b.base - a.base),
    byMonth: periodMonths(period).map((month) => {
      const income = sum(
        incomes.filter((row) => row.day.startsWith(month)),
        (row) => row.base,
      );
      const spent = sum(
        expenses.filter((e) => e.invoice_date.startsWith(month)),
        expenseBaseOf,
      );
      return { month, income, expenses: spent, result: round2(income - spent) };
    }),
  };
};

export type AccountingAlert = {
  key: string;
  text: string;
  expenseIds?: Identifier[];
};

/**
 * What is missing to close the period: invoices from recurring suppliers for the
 * months already over, files and supplier tax ids.
 */
export const buildAlerts = (
  suppliers: AccSupplier[],
  allExpenses: AccExpense[],
  period: Period,
  today: string,
  /** When the period was last sent to the accountant */
  sentAt?: string | null,
): AccountingAlert[] => {
  const expenses = allExpenses.filter((e) => inPeriod(e.invoice_date, period));
  const currentMonth = today.slice(0, 7);
  const closedMonths = periodMonths(period).filter(
    (month) => month < currentMonth,
  );
  const alerts: AccountingAlert[] = [];

  const toReview = expenses.filter((e) => e.status === "review");
  if (toReview.length) {
    alerts.push({
      key: "to-review",
      text: `${toReview.length} ${toReview.length === 1 ? "gasto leído o creado automáticamente pendiente" : "gastos leídos o creados automáticamente pendientes"} de revisar`,
      expenseIds: toReview.map((e) => e.id),
    });
  }
  if (sentAt) {
    const changed = expenses.filter(
      (e) => (e.updated_at ?? e.created_at ?? "") > sentAt,
    );
    if (changed.length) {
      alerts.push({
        key: "after-send",
        text: `${changed.length} ${changed.length === 1 ? "gasto añadido o cambiado" : "gastos añadidos o cambiados"} después de enviarlo al gestor: avísale o vuelve a enviar`,
        expenseIds: changed.map((e) => e.id),
      });
    }
  }

  suppliers
    .filter((supplier) => supplier.recurring)
    .forEach((supplier) => {
      const missing = closedMonths.filter(
        (month) =>
          !allExpenses.some(
            (e) =>
              (e.supplier_id === supplier.id ||
                e.supplier_name.toLowerCase() ===
                  supplier.name.toLowerCase()) &&
              e.invoice_date.startsWith(month),
          ),
      );
      if (missing.length) {
        alerts.push({
          key: `recurring-${supplier.id}`,
          text: `Falta la factura de ${supplier.name}: ${missing
            .map(monthName)
            .join(", ")}`,
        });
      }
    });

  const withoutFile = expenses.filter((e) => !e.file_path);
  if (withoutFile.length) {
    alerts.push({
      key: "no-file",
      text: `${withoutFile.length} ${withoutFile.length === 1 ? "gasto" : "gastos"} sin la factura adjunta`,
      expenseIds: withoutFile.map((e) => e.id),
    });
  }
  const withoutTaxId = expenses.filter(
    (e) => e.vat_mode !== "exempt" && !e.supplier_tax_id?.trim(),
  );
  if (withoutTaxId.length) {
    alerts.push({
      key: "no-tax-id",
      text: `${withoutTaxId.length} ${withoutTaxId.length === 1 ? "factura" : "facturas"} sin el NIF del proveedor (sin NIF no se puede deducir el IVA)`,
      expenseIds: withoutTaxId.map((e) => e.id),
    });
  }
  return alerts;
};

/** Third parties above 3.005,06 € in the year (modelo 347), purchases side. */
export const suppliersOver347 = (allExpenses: AccExpense[], year: number) => {
  const totals = new Map<
    string,
    { name: string; taxId: string; total: number }
  >();
  allExpenses
    .filter(
      (e) =>
        e.vat_mode !== "reverse_charge" &&
        Number(e.invoice_date.slice(0, 4)) === year,
    )
    .forEach((e) => {
      const key = (e.supplier_tax_id?.trim() || e.supplier_name).toLowerCase();
      const current = totals.get(key) ?? {
        name: e.supplier_name,
        taxId: e.supplier_tax_id ?? "",
        total: 0,
      };
      totals.set(key, { ...current, total: current.total + Number(e.total) });
    });
  return [...totals.values()]
    .filter((row) => row.total > 3005.06)
    .map((row) => ({ ...row, total: round2(row.total) }))
    .sort((a, b) => b.total - a.total);
};

const MONTHS = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

/** "2026-10" -> "octubre" */
export const monthName = (month: string) =>
  MONTHS[Number(month.slice(5, 7)) - 1] ?? month;
